import { ReactElement } from 'react'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  FAR_CHART_LINE,
  FAR_ENVELOPE,
  FAR_FOLDER_OPEN,
  FAR_LOCK,
  FAR_PERSON_TO_DOOR,
  FAR_PLUS_CIRCLE,
  FAR_SQUARE_SLIDERS,
  FAR_USER
} from 'const/icons'
import i18n from 'strings/i18n'
import {
  DropdownRow,
  IconWrapper,
  MenuItemText,
  SignoutWrapper
} from './subcomponents'

interface DropdownItem {
  key: string
  shouldDisplay: boolean
  icon: IconProp
  label: string
  onClick: () => void
  dataTest: string
  dataCy: string
  dataReason: string
  dataType?: string
  disabled?: boolean
  wrap?: boolean
}

interface DropdownFactoryParams {
  shouldDisplayDocumentCenter: boolean
  shouldDisplayMissingSynchronyAccount: boolean
  shouldDisplaySignInSecurity: boolean
  alertsVisibleOnMobile: boolean
  isLogoutDisabled: boolean
  onProfileClick: () => void
  onDocumentCenterClick: () => void
  onMarketingClick: () => void
  onAlertsClick: () => void
  onCreditScoreClick: () => void
  onMissingSynchronyAccountClick: () => void
  onSecurityClick: () => void
  onLogoutClick: () => void
}

const getDropdownItems = (params: DropdownFactoryParams): DropdownItem[] => [
  {
    key: 'profile',
    shouldDisplay: true,
    icon: FAR_USER,
    label: i18n({ button: 'profileManagement' }) as string,
    onClick: params.onProfileClick,
    dataTest: 'profile-management-button',
    dataCy: 'profile-management',
    dataReason: 'Profile management',
    dataType: 'account'
  },
  {
    key: 'alerts-mobile',
    shouldDisplay: params.alertsVisibleOnMobile,
    icon: FAR_ENVELOPE,
    label: i18n({ button: 'alerts' }) as string,
    onClick: params.onAlertsClick,
    dataTest: 'alerts-button',
    dataCy: 'alerts-button',
    dataReason: 'alerts'
  },
  {
    key: 'document-center-link',
    shouldDisplay: params.shouldDisplayDocumentCenter,
    icon: FAR_FOLDER_OPEN,
    label: i18n({ button: 'documentCenter' }) as string,
    onClick: params.onDocumentCenterClick,
    dataTest: 'document-center',
    dataCy: 'document-center',
    dataReason: 'document center'
  },
  {
    key: 'marketing',
    shouldDisplay: true,
    icon: FAR_SQUARE_SLIDERS,
    label: i18n({ button: 'marketingSetting' }) as string,
    onClick: params.onMarketingClick,
    dataTest: 'marketing-privacy-setting-button',
    dataCy: 'marketing-privacy-setting',
    dataReason: 'marketing & privacy settings'
  },
  {
    key: 'credit-score',
    shouldDisplay: true,
    icon: FAR_CHART_LINE,
    label: i18n({ button: 'yourCreditScore' }) as string,
    onClick: params.onCreditScoreClick,
    dataTest: 'your-credit-score-button',
    dataCy: 'your-credit-score',
    dataReason: 'credit score'
  },
  {
    key: 'missing-synchrony-account',
    shouldDisplay: params.shouldDisplayMissingSynchronyAccount,
    icon: FAR_PLUS_CIRCLE,
    label: i18n({ button: 'missingSynchronyAccount' }) as string,
    onClick: params.onMissingSynchronyAccountClick,
    dataTest: 'missing-synchrony-account-button',
    dataCy: 'missing-synchrony-account',
    dataReason: 'missing synchrony account'
  },
  {
    key: 'sign-in-security',
    shouldDisplay: params.shouldDisplaySignInSecurity,
    icon: FAR_LOCK,
    label: i18n({ button: 'signInSecurity' }) as string,
    onClick: params.onSecurityClick,
    dataTest: 'sign-in-security-button',
    dataCy: 'sign-in-security',
    dataReason: 'sign in security'
  },
  {
    key: 'sign-out',
    shouldDisplay: true,
    icon: FAR_PERSON_TO_DOOR,
    label: i18n({ button: 'signOut' }) as string,
    onClick: params.onLogoutClick,
    dataTest: 'sign-out-button',
    dataCy: 'sign-out-button',
    dataReason: 'sign out',
    disabled: params.isLogoutDisabled,
    wrap: true
  }
]

const createDropdownItemElement = (item: DropdownItem): ReactElement => (
  <DropdownRow
    key={item.key}
    buttonType="text"
    data-analytics="clicktrackingevent"
    data-object="button"
    data-reason={item.dataReason}
    data-test={item.dataTest}
    data-cy={item.dataCy}
    data-type={item.dataType || 'customer menu'}
    disabled={item.disabled}
    onClick={item.onClick}
  >
    <IconWrapper>
      <FontAwesomeIcon icon={item.icon} />
    </IconWrapper>
    <MenuItemText data-test={`${item.dataTest}-btn`}>{item.label}</MenuItemText>
  </DropdownRow>
)

export const buildDropdownList = (
  params: DropdownFactoryParams
): ReactElement[] => {
  return getDropdownItems(params)
    .filter(({ shouldDisplay }) => shouldDisplay)
    .map((item) =>
      item.wrap ? (
        <SignoutWrapper key={item.key}>
          {createDropdownItemElement(item)}
        </SignoutWrapper>
      ) : (
        createDropdownItemElement(item)
      )
    )
}
